import { ChangeEvent, SyntheticEvent, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';

import {
  Input,
  Select,
  Patterns,
  Alert,
  Label,
  Checkbox,
  NumberInput,
  Divider,
  RadioList,
} from '@treezor/design-system';

import { Parent } from '@/features/user/components/Parent';
import { TCountryCode, EParentType, UserTypes } from '@/features/user/store/user.model';
import { useParentUserInput } from '@/features/user/hooks/createUser/useParentUserInput';
import { defaultTaxResidence } from '@/features/user/components/CreateUser/CreateUserTaxResidences';
import { AddressSummary } from '@/features/user/components/CreateUser/AddressSummary';
import { ContentLayout, TwoColumns, CustomColumns } from '@/features/user/components/CreateUser/layout/ContentLayout';
import { CreateUserFooter } from '@/features/user/components/CreateUser/layout/CreateUserFooter';
import { IPostCreateUser } from '@/features/user/contexts/CreateUserContext';
import { CreateUserEmptyComponent } from '@/features/user/components/CreateUser/layout/CreateUserEmptyComponent';
import { AdditonalDataLayout } from '@/features/user/components/CreateUser/layout/AddtionalData';
import { useFieldOptions } from '@/features/user/hooks/createUser/useFieldOptions';
import { useCreateUser } from '@/features/user/hooks/createUser/useCreateUser';
import { Contact } from '@/features/user/components/CreateUser/Contact';
import { useMailInput } from '@/features/user/hooks/createUser/useMailInput';

import { useAppSelector } from '@/hooks/redux-helpers';

import { selectProfileByUserId } from '@/store/selectors';
import { ICreateUserPartProps } from '@/features/user/store/createUser.types';

export const userInformationDefaultValues: IPostCreateUser = {
  email: '',
  title: undefined,
  distributionCountry: undefined,
  country: undefined,
  firstname: '',
  lastname: '',
  middleName1: '',
  middleName2: '',
  address1: '',
  address2: '',
  address3: '',
  postcode: '',
  city: '',
  secondaryCountry: undefined,
  secondaryAddress1: '',
  secondaryAddress2: '',
  secondaryAddress3: '',
  secondaryPostcode: '',
  secondaryCity: '',
  phone: '',
  mobile: '',
  roles: [],
  isSameAsPrincipalAddress: 'yes',
  parentType: '',
  effectiveBeneficiary: undefined,
  isAttachedToParent: undefined,
  parentUserId: undefined,
};

export function CreateUserPhysicalUserInformation({ stepsLength }: ICreateUserPartProps) {
  const { t } = useTranslation();

  const {
    config,
    distributionCountry,
    handleSubmit,
    watches,
    user,
    updateUser,
    register,
    touchedFields,
    inputMaxLength,
    handleInputChange,
    handleValueChange,
    hasValidField,
    hasInvalidInputLength,
    navigateDashboard,
    inputMaxNumber,
    updateParentUser,
    parentUser,
    hasDistributionCountry,
    isDistributionCountryDisabled,
  } = useCreateUser(userInformationDefaultValues);

  const parentUserFromStore = useAppSelector(
    (state) => selectProfileByUserId(state, user.parentUserId)?.state?.user?.state,
  );

  const {
    countryCodeOptions,
    persoAndBusinessAddressOptions,
    persoAndParentAddressOptions,
    userHierarchicalRelationOptions,
    titleOptions,
  } = useFieldOptions();

  const emailWatch = watches['email'];
  const lastNameWatch = watches['lastname'];
  const firstNameWatch = watches['firstname'];
  const distributionCountryWatch = watches['distributionCountry'];
  const phoneWatch = watches['phone'];
  const mobileWatch = watches['mobile'];
  const address1Watch = watches['address1'];
  const address2Watch = watches['address2'];
  const address3Watch = watches['address3'];
  const secondaryAddress1Watch = watches['secondaryAddress1'];
  const secondaryAddress2Watch = watches['secondaryAddress2'];
  const secondaryAddress3Watch = watches['secondaryAddress3'];
  const rolesWatch = watches['roles'];
  const isSameAsPrincipalAddressWatch = watches['isSameAsPrincipalAddress'];
  const parentTypeWatch = watches['parentType'];
  const effectiveBeneficiaryWatch = watches['effectiveBeneficiary'];
  const isAttachedToParentWatch = watches['isAttachedToParent'];
  const parentUserIdWatch = watches['parentUserId'];

  const {
    iconStatus,
    error: emailError,
    handleAsyncBlur: handleEmailBlur,
  } = useMailInput({ initialValue: emailWatch });

  const {
    iconStatus: parentUserIconStatus,
    status: parentUserStatus,
    handleAsyncBlur: handleParentUserIdBlur,
    data: parentUserData,
  } = useParentUserInput({ initialValue: parentUserIdWatch });

  const onChangeUserHierarchicalRelation = useCallback(
    (value: 'yes' | 'no') => {
      handleValueChange(value, 'isAttachedToParent');
      if (value === 'no') {
        handleValueChange(undefined, 'parentUserId');
        updateParentUser(undefined);
      }
    },
    [handleValueChange, updateParentUser],
  );

  useEffect(() => {
    if (parentUserIdWatch) {
      if (parentUserFromStore) {
        updateParentUser(parentUserFromStore);
      } else {
        updateParentUser(parentUserData ? parentUserData[0] : undefined);
      }
    }
  }, [parentUserData, parentUserFromStore, parentUserIdWatch, updateParentUser]);

  const onRoleChange = useCallback(
    (role: EParentType, isChecked: boolean) => {
      const newRoles = isChecked ? [...rolesWatch, role] : rolesWatch.filter((r) => r !== role);

      let parentType = '';
      let controllingPersonType = undefined;
      let taxResidences = user.taxResidences;

      if (newRoles?.length && !newRoles.includes(EParentType.leader) && !user.taxResidences) {
        taxResidences = [{ ...defaultTaxResidence, isDirty: true }];
      }

      if (
        (!newRoles?.length || newRoles.includes(EParentType.leader)) &&
        user.taxResidences?.length === 1 &&
        !!user.taxResidences[0]?.isDirty &&
        !user.taxResidences[0]?.country
      ) {
        taxResidences = [];
      }

      if (newRoles.includes(EParentType.employee)) {
        parentType = EParentType.employee;
      } else if (newRoles.includes(EParentType.shareholder) && newRoles.includes(EParentType.leader)) {
        parentType = EParentType.leader;
        controllingPersonType = 3;
      } else {
        parentType = newRoles[0];
        controllingPersonType = newRoles.includes(EParentType.shareholder) ? 1 : 3;
      }

      handleValueChange(parentType, 'parentType');
      handleValueChange(newRoles, 'roles');
      handleValueChange(controllingPersonType, 'controllingPersonType');
      handleValueChange(taxResidences, 'taxResidences');
    },
    [handleValueChange, rolesWatch, user.taxResidences],
  );

  const onChangeIsSameAsPrincipalAddress = useCallback(
    (value: 'yes' | 'no') => {
      handleValueChange(value, 'isSameAsPrincipalAddress');
    },
    [handleValueChange],
  );

  const isFormDisabled = useMemo(
    () =>
      !emailWatch ||
      !!emailError ||
      !firstNameWatch ||
      !lastNameWatch ||
      (hasDistributionCountry && !distributionCountryWatch && !isDistributionCountryDisabled) ||
      hasInvalidInputLength(['address1', 'address2', 'address3']) ||
      hasInvalidInputLength(['secondaryAddress1', 'secondaryAddress2', 'secondaryAddress3']) ||
      (!!mobileWatch && !Patterns.phone.test(mobileWatch)) ||
      (!!phoneWatch && !Patterns.phone.test(phoneWatch)) ||
      (parentUser?.userTypeId === UserTypes.business && !parentTypeWatch) ||
      !!inputMaxNumber(effectiveBeneficiaryWatch, 100)?.error ||
      (!user.isAttachedToParent &&
        (isAttachedToParentWatch === undefined || (isAttachedToParentWatch === 'yes' && !parentUser))),
    [
      emailWatch,
      emailError,
      firstNameWatch,
      lastNameWatch,
      hasDistributionCountry,
      isDistributionCountryDisabled,
      distributionCountryWatch,
      hasInvalidInputLength,
      mobileWatch,
      phoneWatch,
      parentUser,
      parentTypeWatch,
      inputMaxNumber,
      effectiveBeneficiaryWatch,
      user.isAttachedToParent,
      isAttachedToParentWatch,
    ],
  );

  return (
    <div className="flex flex-col gap-6">
      <ContentLayout title={t('pages.dashboard.createUser.form.blocks.hierarchicalRelation')}>
        {!parentUserFromStore && (
          <div className="flex flex-col gap-2">
            <p className="typo-body-medium-paragraph">{t('pages.dashboard.createUser.form.isAttachedToParent')}</p>
            <div className="flex gap-3">
              <RadioList
                name="isAttachedToParentWatch"
                options={userHierarchicalRelationOptions}
                onChange={(value: 'yes' | 'no') => {
                  onChangeUserHierarchicalRelation(value);
                }}
                value={isAttachedToParentWatch}
                className="pt-2 gap-3"
                direction="row"
              />
              <Input
                type="text"
                {...(parentUserStatus === 'pending' ? { icon: { right: parentUserIconStatus } } : {})}
                placeholder={t('pages.dashboard.createUser.form.parentUserIdPlaceholder')}
                {...register('parentUserId')}
                onBlur={handleParentUserIdBlur}
                size="s"
                disabled={isAttachedToParentWatch !== 'yes'}
              />
            </div>
          </div>
        )}
        {parentUser && <Parent redirectToParent={navigateDashboard} parentUser={parentUser} />}
        {parentUserStatus === 'error' && (
          <Alert
            variant="secondary"
            type="danger"
            description={t('pages.dashboard.createUser.form.errors.noUserFound')}
            title=""
            isToast={false}
          />
        )}
      </ContentLayout>
      {(user.isAttachedToParent ||
        (isAttachedToParentWatch === 'yes' && parentUser) ||
        isAttachedToParentWatch === 'no') && (
        <>
          <ContentLayout title={t('pages.dashboard.createUser.form.blocks.mainInformation')}>
            <Input
              type="text"
              icon={{ left: 'mail', right: iconStatus }}
              label={t('pages.dashboard.createUser.form.email')}
              required
              {...register('email', { pattern: Patterns.email })}
              error={emailError}
              onBlur={handleEmailBlur}
            />
            <CustomColumns columns={[1, 2, 2]}>
              <Select
                label={t('pages.dashboard.createUser.form.title')}
                options={titleOptions}
                defaultValue={user?.title}
                onChange={(value: string) => handleValueChange(value, 'title')}
                showDelete
              />
              <Input
                type="text"
                label={t('pages.dashboard.createUser.form.lastname')}
                required
                {...register('lastname')}
                onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                  handleInputChange(event, 'lastname')
                }
              />
              <Input
                type="text"
                label={t('pages.dashboard.createUser.form.firstname')}
                required
                {...register('firstname')}
                onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                  handleInputChange(event, 'firstname')
                }
              />
            </CustomColumns>
            <AdditonalDataLayout
              shouldShow={hasValidField(['middleName1', 'middleName2'])}
              label={t('pages.dashboard.createUser.form.actions.addMiddleNames')}
            >
              <TwoColumns>
                <Input
                  type="text"
                  label={t('pages.dashboard.createUser.form.middleName')}
                  {...register('middleName1')}
                  onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                    handleInputChange(event, 'middleName1')
                  }
                />
                <Input
                  type="text"
                  label={t('pages.dashboard.createUser.form.middleName')}
                  {...register('middleName2')}
                  onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                    handleInputChange(event, 'middleName2')
                  }
                />
              </TwoColumns>
            </AdditonalDataLayout>
            {parentUser && parentUser.userTypeId === UserTypes.business && (
              <div className="flex flex-col gap-2 items-start justify-start">
                <Label isRequired className="typo-body-medium-paragraph">
                  {t('pages.dashboard.createUser.form.role')}
                </Label>
                <Checkbox
                  label={t('pages.dashboard.createUser.form.roles.leader')}
                  onChange={(event: ChangeEvent<HTMLInputElement>) =>
                    onRoleChange(EParentType.leader, event?.target?.checked)
                  }
                  checked={watches['roles'].includes(EParentType.leader)}
                  disabled={watches['roles'].includes(EParentType.employee)}
                  className="inline-block"
                />
                <div className="flex gap-3">
                  <Checkbox
                    label={t('pages.dashboard.createUser.form.roles.shareholderHolding')}
                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                      onRoleChange(EParentType.shareholder, event?.target?.checked)
                    }
                    checked={watches['roles'].includes(EParentType.shareholder)}
                    disabled={watches['roles'].includes(EParentType.employee)}
                  />
                  <NumberInput
                    label=""
                    defaultValue={watches['effectiveBeneficiary']}
                    placeholder="..."
                    inputProps={{
                      ...register('effectiveBeneficiary'),
                    }}
                    unit={
                      <div
                        className={twMerge(
                          'flex gap-2 items-center h-[20px]',
                          !watches['roles'].includes(EParentType.shareholder) && 'text-disabled',
                        )}
                      >
                        <Divider direction="vertical" />
                        <div>%</div>
                      </div>
                    }
                    size="s"
                    className="!w-[22px]"
                    disabled={!watches['roles'].includes(EParentType.shareholder)}
                    onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                      handleValueChange(event.currentTarget.value, 'effectiveBeneficiary')
                    }
                    {...inputMaxNumber(watches['effectiveBeneficiary'], 100)}
                  />
                </div>
                <Checkbox
                  label={t('pages.dashboard.createUser.form.roles.employee')}
                  onChange={(event: ChangeEvent<HTMLInputElement>) =>
                    onRoleChange(EParentType.employee, event?.target?.checked)
                  }
                  checked={watches['roles'].includes(EParentType.employee)}
                  className="mt-1"
                  disabled={
                    watches['roles'].includes(EParentType.shareholder) || watches['roles'].includes(EParentType.leader)
                  }
                />
              </div>
            )}
            {hasDistributionCountry && (
              <Select
                label={t('pages.currentProfiles.profile.informations.distributionCountry')}
                required
                withSearch
                options={countryCodeOptions.filter(({ value }) => distributionCountry.includes(value))}
                defaultValue={user?.distributionCountry || parentUser?.distributionCountry}
                disabled={isDistributionCountryDisabled}
                noOptionsComponent={<CreateUserEmptyComponent />}
                onChange={(value: string) => handleValueChange(value, 'distributionCountry')}
              />
            )}
          </ContentLayout>
          <ContentLayout title={t('pages.dashboard.createUser.form.blocks.address')}>
            <div>
              <span className="typo-h6-bold">{t('pages.dashboard.createUser.form.mainAddress')}</span>
            </div>
            {config?.settings?.users?.tabs?.includes('cards') && (
              <Alert
                title={t('pages.dashboard.createUser.form.deliveryAddress')}
                description={t('pages.dashboard.createUser.form.deliveryAddressDescription')}
                variant="secondary"
              />
            )}
            {parentUser && (
              <>
                <RadioList
                  name="isSameAsPrincipalAddress"
                  options={
                    parentUser.userTypeId === UserTypes.naturalPerson
                      ? persoAndParentAddressOptions
                      : persoAndBusinessAddressOptions
                  }
                  onChange={(value: 'yes' | 'no') => {
                    onChangeIsSameAsPrincipalAddress(value);
                  }}
                  value={isSameAsPrincipalAddressWatch}
                  className="pt-2 gap-3"
                  direction="row"
                />
                {isSameAsPrincipalAddressWatch === 'yes' && (
                  <AddressSummary
                    title={t('pages.dashboard.createUser.form.blocks.address')}
                    address1={parentUser.address1}
                    address2={parentUser.address2}
                    address3={parentUser.address3}
                    city={parentUser.city}
                    country={parentUser.country}
                    postcode={parentUser.postcode}
                  />
                )}
              </>
            )}
            {(!parentUser || isSameAsPrincipalAddressWatch === 'no') && (
              <>
                <Select
                  label={t('pages.currentProfiles.profile.contact.country')}
                  options={countryCodeOptions}
                  withSearch
                  defaultValue={user?.country}
                  onChange={(value: string) => {
                    handleValueChange(value, 'country');
                    updateUser({ ...user, country: value as TCountryCode });
                  }}
                  noOptionsComponent={<CreateUserEmptyComponent />}
                  showDelete
                />
                <TwoColumns>
                  <Input
                    value={address1Watch}
                    type="text"
                    label={t('pages.dashboard.createUser.form.address1')}
                    {...register('address1')}
                    onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                      handleInputChange(event, 'address1')
                    }
                    {...inputMaxLength(address1Watch, 35)}
                  />
                  <Input
                    value={address2Watch}
                    type="text"
                    label={t('pages.dashboard.createUser.form.address2')}
                    {...register('address2')}
                    onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                      handleInputChange(event, 'address2')
                    }
                    {...inputMaxLength(address2Watch, 35)}
                  />
                </TwoColumns>
                <Input
                  value={address3Watch}
                  type="text"
                  label={t('pages.dashboard.createUser.form.address3')}
                  {...register('address3')}
                  onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                    handleInputChange(event, 'address3')
                  }
                  {...inputMaxLength(address3Watch, 35)}
                />
                <TwoColumns>
                  <Input
                    type="text"
                    label={t('pages.currentProfiles.profile.contact.postcode')}
                    {...register('postcode')}
                    onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                      handleInputChange(event, 'postcode')
                    }
                    defaultValue={user?.postcode}
                  />
                  <Input
                    type="text"
                    label={t('pages.currentProfiles.profile.contact.city')}
                    {...register('city')}
                    onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                      handleInputChange(event, 'city')
                    }
                  />
                </TwoColumns>
              </>
            )}
            <AdditonalDataLayout
              shouldShow={hasValidField([
                'secondaryAddress1',
                'secondaryAddress2',
                'secondaryAddress3',
                'secondaryPostcode',
                'secondaryCity',
                'secondaryCountry',
              ])}
              label={t('pages.dashboard.createUser.form.actions.addSecondaryAddress')}
            >
              <div>
                <span className="typo-h6-bold">{t('pages.dashboard.createUser.form.secondaryAddress')}</span>
              </div>
              <Select
                label={t('pages.currentProfiles.profile.contact.country')}
                options={countryCodeOptions}
                withSearch
                defaultValue={user?.secondaryCountry}
                onChange={(value: string) => handleValueChange(value, 'secondaryCountry')}
                noOptionsComponent={<CreateUserEmptyComponent />}
                showDelete
              />
              <TwoColumns>
                <Input
                  value={secondaryAddress1Watch}
                  type="text"
                  label={t('pages.dashboard.createUser.form.address1')}
                  {...register('secondaryAddress1')}
                  onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                    handleInputChange(event, 'secondaryAddress1')
                  }
                  {...inputMaxLength(secondaryAddress1Watch, 35)}
                />
                <Input
                  type="text"
                  value={secondaryAddress2Watch}
                  label={t('pages.dashboard.createUser.form.address2')}
                  {...register('secondaryAddress2')}
                  onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                    handleInputChange(event, 'secondaryAddress2')
                  }
                  {...inputMaxLength(secondaryAddress2Watch, 35)}
                />
              </TwoColumns>
              <Input
                type="text"
                value={secondaryAddress3Watch}
                label={t('pages.dashboard.createUser.form.address3')}
                {...register('secondaryAddress3')}
                onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                  handleInputChange(event, 'secondaryAddress3')
                }
                {...inputMaxLength(secondaryAddress3Watch, 35)}
              />
              <TwoColumns>
                <Input
                  type="text"
                  label={t('pages.currentProfiles.profile.contact.postcode')}
                  {...register('secondaryPostcode')}
                  onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                    handleInputChange(event, 'secondaryPostcode')
                  }
                  defaultValue={user?.secondaryPostcode}
                />
                <Input
                  type="text"
                  label={t('pages.currentProfiles.profile.contact.city')}
                  {...register('secondaryCity')}
                  onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                    handleInputChange(event, 'secondaryCity')
                  }
                />
              </TwoColumns>
            </AdditonalDataLayout>
          </ContentLayout>
          <ContentLayout title={t('pages.dashboard.createUser.form.blocks.contact')}>
            <TwoColumns>
              <Contact
                label={t('pages.dashboard.createUser.form.phone')}
                value={phoneWatch}
                initialValue={user?.phone}
                initialCountry={!touchedFields?.['phone'] && !phoneWatch && distributionCountryWatch}
                handleValueChange={handleValueChange}
                {...register('phone')}
              />
              <Contact
                label={t('pages.dashboard.createUser.form.mobile')}
                value={mobileWatch}
                initialValue={user?.mobile}
                initialCountry={!touchedFields?.['mobile'] && !mobileWatch && distributionCountryWatch}
                handleValueChange={handleValueChange}
                {...register('mobile')}
              />
            </TwoColumns>
          </ContentLayout>
        </>
      )}

      <CreateUserFooter isNextDisabled={isFormDisabled} onNext={handleSubmit} stepsLength={stepsLength} />
    </div>
  );
}
